import React from 'react';
import { FileDown, Printer } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { formatDate } from '../../lib/dateUtils';
import { EGG_LABELS } from './constants';

/**
 * Modal de detalle de factura con desglose de items, rotos y botones de impresión.
 */
export const FacturaDetailDialog = ({ factura, onClose, onExportPDF, empresa }) => (
  <Dialog open={!!factura} onOpenChange={(o) => { if (!o) onClose(); }}>
    <DialogContent className="max-w-xl" data-testid="modal-detalle-factura">
      <DialogHeader>
        <DialogTitle style={{ fontFamily: 'Space Grotesk' }} className="space-y-0.5">
          <div>{factura?.numero_factura}</div>
          {empresa?.rnc && (
            <div className="text-xs font-normal text-muted-foreground tabular-nums">
              RNC: {empresa.rnc}
            </div>
          )}
          {factura?.fecha && (
            <div className="text-xs font-normal text-muted-foreground">
              {formatDate(factura.fecha)}
            </div>
          )}
        </DialogTitle>
      </DialogHeader>
      {factura && (
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-muted-foreground">Cliente</p>
              <p className="font-medium">{factura.cliente_nombre}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Estado</p>
              <p className="font-medium">{factura.estado_pago || 'Pendiente'}</p>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="text-xs font-semibold text-muted-foreground mb-2">ITEMS</h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Lote</TableHead>
                  <TableHead className="text-right">Cant.</TableHead>
                  <TableHead className="text-right">Precio/u</TableHead>
                  <TableHead className="text-right">Subtotal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {factura.items?.map((item, i) => (
                  <TableRow key={i}>
                    <TableCell className="capitalize">{EGG_LABELS[item.tipo_huevo] || item.tipo_huevo}</TableCell>
                    <TableCell className="text-xs">
                      {item.lote_codigo ? (
                        <Badge variant="outline" className="font-mono">
                          {item.lote_codigo}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground italic">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Number(item.cantidad || 0).toLocaleString('en-US')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      ${Number(item.precio_unitario || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">
                      ${Number(item.subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {factura.huevos_rotos?.length > 0 && (
            <div className="bg-destructive/5 border border-destructive/20 rounded-md p-3">
              <h4 className="text-xs font-semibold text-destructive mb-2">HUEVOS ROTOS DEL DESPACHO</h4>
              {(() => {
                const itemsMap = (factura.items || []).reduce((acc, it) => {
                  acc[it.tipo_huevo] = Number(it.precio_unitario) || 0;
                  return acc;
                }, {});
                let totalCantRota = 0;
                let totalMontoRoto = 0;
                return (
                  <>
                    <div className="space-y-1 text-sm">
                      {factura.huevos_rotos.map((roto, i) => {
                        const cant = Number(roto.cantidad_rota ?? roto.cantidad ?? 0);
                        const precio = itemsMap[roto.tipo_huevo] || 0;
                        const monto = cant * precio;
                        totalCantRota += cant;
                        totalMontoRoto += monto;
                        return (
                          <div key={i} className="flex justify-between items-center py-1">
                            <span className="capitalize">{EGG_LABELS[roto.tipo_huevo] || roto.tipo_huevo}</span>
                            <span className="flex items-center gap-3 tabular-nums">
                              <Badge variant="destructive" className="text-xs">
                                {cant.toLocaleString('en-US')} u.
                              </Badge>
                              <span className="text-xs text-muted-foreground">×</span>
                              <span className="text-xs text-muted-foreground">
                                ${precio.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                              <span className="text-xs text-muted-foreground">=</span>
                              <span className="font-semibold text-destructive">
                                ${monto.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <Separator className="my-2 bg-destructive/20" />
                    <div className="flex justify-between items-center text-sm font-semibold">
                      <span>Total rotos</span>
                      <span className="tabular-nums">
                        <span className="text-muted-foreground mr-3">
                          {totalCantRota.toLocaleString('en-US')} u.
                        </span>
                        <span className="text-destructive">
                          ${totalMontoRoto.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {factura.observaciones && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground mb-1">OBSERVACIONES</h4>
              <p className="text-sm">{factura.observaciones}</p>
            </div>
          )}

          <Separator />

          <div className="flex justify-between items-center">
            <span className="font-medium">Total</span>
            <span className="text-xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk' }}>
              ${factura.total_monto?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Copia para el Cliente (sin huevos rotos)
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="default"
                className="w-full"
                onClick={() => onExportPDF(factura, 'print', 'cliente')}
                data-testid="btn-detalle-imprimir-cliente"
              >
                <Printer className="w-4 h-4 mr-2" /> Imprimir
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => onExportPDF(factura, 'save', 'cliente')}
                data-testid="btn-detalle-pdf-cliente"
              >
                <FileDown className="w-4 h-4 mr-2" /> Descargar PDF
              </Button>
            </div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">
              Original (con huevos rotos)
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => onExportPDF(factura, 'print', 'interna')}
                data-testid="btn-detalle-imprimir-interna"
              >
                <Printer className="w-4 h-4 mr-2" /> Imprimir
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => onExportPDF(factura, 'save', 'interna')}
                data-testid="btn-detalle-pdf-interna"
              >
                <FileDown className="w-4 h-4 mr-2" /> Descargar PDF
              </Button>
            </div>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
